import React, { useState, useCallback, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import MenuSourceCard from '../components/MenuSourceCard';
import MenuEditor from '../components/MenuEditor';
import ConfigPanel, { TEMPLATES, DEFAULT_TYPE_SCALE } from '../components/ConfigPanel';
import MenuPreview from '../components/MenuPreview';
import VenueBrandPanel from '../components/VenueBrandPanel';
import { formatParsedMenu } from '../utils/menuTextFormat';

const MEAL_TYPES = [
  'Cocktails',
  'Liquor',
  'Lunch Features',
  'Dinner Features',
  'Desserts',
  'Weekend Features',
  'Brunch Features',
  'Specials'
];

const DEMO_MENU = {
  categories: [
    {
      name: 'Starters',
      items: [
        {
          name: 'Charred Broccolini',
          description: 'Lemon labneh, toasted almonds, chili oil',
          price: '$14'
        },
        {
          name: 'Tuna Crudo',
          description: 'Citrus, fennel, capers, cold-pressed olive oil',
          price: '$18'
        }
      ]
    },
    {
      name: 'Mains',
      items: [
        {
          name: 'Braised Short Rib',
          description: 'Parsnip puree, roasted carrots, red wine glaze',
          price: '$34'
        },
        {
          name: 'Wild Mushroom Bucatini',
          description: 'Brown butter, thyme, pecorino, crispy shallots',
          price: '$26'
        }
      ]
    },
    {
      name: 'Desserts',
      items: [
        {
          name: 'Olive Oil Cake',
          description: 'Whipped mascarpone, poached berries',
          price: '$11'
        },
        {
          name: 'Dark Chocolate Tart',
          description: 'Sea salt caramel, creme fraiche',
          price: '$12'
        }
      ]
    }
  ]
};

const LETTER_WIDTH_MM = 215.9;
const LETTER_HEIGHT_MM = 279.4;
const PRINT_IFRAME_ID = 'menutide-print-frame';
const DRAFT_STORAGE_KEY = 'menutide-menu-draft';
const DRAFT_TTL_MS = 2 * 60 * 60 * 1000;
const DRAFT_VERSION = 1;
const HISTORY_STORAGE_KEY = 'menutide-feature-history';
const AUTO_SAVE_MEAL_TYPES = new Set(['Lunch Features', 'Dinner Features']);
const MAX_HISTORY_RECORDS = 10;

let sourceIdCounter = Date.now();

function createSource() {
  return {
    id: `src-${sourceIdCounter++}`,
    label: '',
    inputType: null, // 'photo' | 'text'
    rawText: '',
    textInput: '',
    categories: [],
    status: 'pending' // 'pending' | 'parsing' | 'done' | 'error'
  };
}

function readStoredDraft() {
  if (typeof window === 'undefined') return null;

  try {
    const rawDraft = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!rawDraft) return null;

    const draft = JSON.parse(rawDraft);
    if (draft.version !== DRAFT_VERSION || !draft.expiresAt || Date.now() > draft.expiresAt) {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      return null;
    }

    return draft;
  } catch (err) {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    return null;
  }
}

function readLocalHistory() {
  if (typeof window === 'undefined') return [];

  try {
    const rawHistory = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    const history = rawHistory ? JSON.parse(rawHistory) : [];
    return Array.isArray(history) ? limitHistory(history) : [];
  } catch (err) {
    window.localStorage.removeItem(HISTORY_STORAGE_KEY);
    return [];
  }
}

function writeLocalHistory(history) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(limitHistory(history)));
}

function sortHistory(history) {
  return [...history].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
}

function limitHistory(history) {
  return sortHistory(history).slice(0, MAX_HISTORY_RECORDS);
}

function createLocalId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatHistoryDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getFeatureDateLabel(date = new Date()) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
}

function getAutoHistoryMeta(mealType, date = new Date()) {
  if (!AUTO_SAVE_MEAL_TYPES.has(mealType)) return null;

  return {
    autoKey: `${getLocalDateKey(date)}::${mealType}`,
    title: `${mealType} - ${getFeatureDateLabel(date)}`
  };
}

export default function MenuGenerator({ venue, onVenueChange, setHeaderControls }) {
  const previewContainerRef = useRef(null);
  const restoredDraftRef = useRef(readStoredDraft());
  const autoSaveTimerRef = useRef(null);

  const getDefaultMealType = () => {
    const hour = new Date().getHours();
    return hour < 12 ? 'Lunch Features' : 'Dinner Features';
  };

  const getDefaultConfig = () => ({
    mealType: getDefaultMealType(),
    template: 'heritage',
    layout: 'half',       // 'full' | 'half'
    pages: 'front-only',  // 'front-only' | 'front-back'
    backCategories: [],    // category names assigned to back page
    typeScale: {
      ...DEFAULT_TYPE_SCALE
    },
    descriptionBold: true
  });

  const restoredDraft = restoredDraftRef.current;
  const [step, setStep] = useState(restoredDraft?.step || 1);
  const [sources, setSources] = useState(
    restoredDraft?.sources?.length ? restoredDraft.sources : [createSource()]
  );
  const [previewZoom, setPreviewZoom] = useState(restoredDraft?.previewZoom || 1);

  // Merged + edited menu
  const [menu, setMenu] = useState(restoredDraft?.menu?.categories ? restoredDraft.menu : { categories: [] });

  // Output config
  const [config, setConfig] = useState({
    ...getDefaultConfig(),
    ...(restoredDraft?.config || {})
  });

  const [historyRecords, setHistoryRecords] = useState(readLocalHistory);
  const [historyStatus, setHistoryStatus] = useState('loading');
  const [historyNotice, setHistoryNotice] = useState('');
  const [activeHistoryId, setActiveHistoryId] = useState(restoredDraft?.activeHistoryId || null);
  const [historyTitle, setHistoryTitle] = useState(restoredDraft?.historyTitle || '');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [spellChecking, setSpellChecking] = useState(false);

  useEffect(() => {
    if (restoredDraft?.venue) {
      onVenueChange(restoredDraft.venue);
    }
  }, [onVenueChange, restoredDraft]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const draft = {
      version: DRAFT_VERSION,
      savedAt: Date.now(),
      expiresAt: Date.now() + DRAFT_TTL_MS,
      step,
      sources,
      menu,
      config,
      venue,
      previewZoom,
      activeHistoryId,
      historyTitle
    };

    try {
      window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    }

    return undefined;
  }, [activeHistoryId, config, historyTitle, menu, previewZoom, sources, step, venue]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const clearExpiredDraft = () => {
      const draft = readStoredDraft();
      if (!draft) {
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    };

    const intervalId = window.setInterval(clearExpiredDraft, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch('/menu-history');
        if (!res.ok) {
          throw new Error('History server unavailable');
        }

        const data = await res.json();
        const nextHistory = limitHistory(data.history || []);
        setHistoryRecords(nextHistory);
        writeLocalHistory(nextHistory);
        setHistoryStatus(data.storage === 'cloud' ? 'server' : 'local');
      } catch (err) {
        setHistoryRecords(readLocalHistory());
        setHistoryStatus('local');
      }
    };

    loadHistory();
  }, []);

  useEffect(() => () => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const autoMeta = getAutoHistoryMeta(config.mealType);
    const hasItems = menu.categories?.some(category => category.items?.length > 0);

    if (!autoMeta || !hasItems || historyStatus === 'loading') {
      return undefined;
    }

    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveHistoryRecord(autoMeta);
    }, 1600);

    return () => {
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [config, historyStatus, menu, venue]);

  // ── Source management ──
  const updateSource = useCallback((id, updates) => {
    setSources(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  }, []);

  const addSource = () => {
    setSources(prev => [...prev, createSource()]);
  };

  const removeSource = (id) => {
    setSources(prev => prev.filter(s => s.id !== id));
  };

  // ── Step transitions ──
  const goToEdit = () => {
    // Merge all parsed categories from all sources
    const allCategories = [];
    sources.forEach(src => {
      if (src.status === 'done' && src.categories.length > 0) {
        src.categories.forEach(cat => {
          allCategories.push({ ...cat, items: cat.items.map(i => ({ ...i })) });
        });
      }
    });
    setMenu({ categories: allCategories });
    setStep(2);
  };

  const [showHistoryDrawer, setShowHistoryDrawer] = useState(true);

  const loadDemoMenu = () => {
    // Auto-save user's current menu if it contains items so work is never lost!
    const hasItems = menu.categories?.some(c => c.items?.length > 0);
    if (hasItems) {
      const autoMeta = {
        title: `${config.mealType || 'Menu'} (Draft ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`,
        mealType: config.mealType || 'Dinner Features'
      };
      const draftPayload = buildHistoryPayload(autoMeta);
      upsertLocalHistoryRecord(draftPayload);
    }

    const demoCategories = DEMO_MENU.categories.map(category => ({
      ...category,
      items: category.items.map(item => ({ ...item }))
    }));

    setMenu({ categories: demoCategories });
    setHistoryTitle('Demo Menu');
    setActiveHistoryId(null);
    setStep(2);
    setHistoryNotice(hasItems ? 'Loaded Demo Menu. Your previous menu draft has been auto-saved in Version History!' : 'Loaded Demo Menu.');
  };

  const goToPreview = () => {
    setStep(3);
  };

  const goBack = (toStep) => {
    setStep(toStep);
  };

  // ── PDF output ──
  const waitForPreviewFonts = async () => {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await new Promise(resolve => window.requestAnimationFrame(() => resolve()));
  };

  const buildMenuPdf = async () => {
    await waitForPreviewFonts();

    const previewRoot = previewContainerRef.current;
    const pageElements = previewRoot
      ? Array.from(previewRoot.querySelectorAll('.tpl-page'))
      : [];

    if (pageElements.length === 0) {
      throw new Error('Preview pages are not ready yet');
    }

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
      compress: true
    });

    for (let index = 0; index < pageElements.length; index += 1) {
      const pageElement = pageElements[index];
      const exportRoot = document.createElement('div');
      exportRoot.className = 'pdf-export-root';

      try {
        const clone = pageElement.cloneNode(true);
        clone.classList.add('pdf-export-page');
        exportRoot.appendChild(clone);
        document.body.appendChild(exportRoot);

        const canvas = await html2canvas(clone, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false
        });

        const imageData = canvas.toDataURL('image/png');
        if (index > 0) {
          pdf.addPage('letter', 'portrait');
        }
        pdf.addImage(imageData, 'PNG', 0, 0, LETTER_WIDTH_MM, LETTER_HEIGHT_MM, undefined, 'FAST');
      } finally {
        if (exportRoot.parentNode) {
          document.body.removeChild(exportRoot);
        }
      }
    }

    const filename = `${venue?.name || 'menu'}-${config.mealType.replace(/\s+/g, '-').toLowerCase()}.pdf`;
    return { pdf, filename };
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const { pdf, filename } = await buildMenuPdf();
      pdf.save(filename);
    } catch (err) {
      alert(err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      window.print();
    } catch (err) {
      console.error('Print trigger failed:', err);
    } finally {
      setPrintLoading(false);
    }
  };

  const handleSpellCheck = async () => {
    if (!menu.categories?.length) return;

    setSpellChecking(true);
    try {
      const res = await fetch('/spell-check-menu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ menu })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to spell check menu');
      }

      const data = await res.json();
      if (data.menu?.categories) {
        setMenu(formatParsedMenu(data.menu));
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSpellChecking(false);
    }
  };

  const getDefaultHistoryTitle = () => {
    const autoMeta = getAutoHistoryMeta(config.mealType);
    if (autoMeta) return autoMeta.title;

    return `${config.mealType || 'Feature Menu'} - ${getFeatureDateLabel()}`;
  };

  const buildHistoryPayload = (overrides = {}) => ({
    title: overrides.title || historyTitle.trim() || getDefaultHistoryTitle(),
    mealType: config.mealType,
    menu,
    config,
    venue,
    autoKey: overrides.autoKey || null
  });

  const upsertLocalHistoryRecord = (payload, id = null) => {
    const now = new Date().toISOString();
    const existing = id
      ? historyRecords.find(record => record.id === id)
      : historyRecords.find(record => payload.autoKey && record.autoKey === payload.autoKey);
    const record = {
      id: existing?.id || id || createLocalId(),
      title: payload.title,
      mealType: payload.mealType,
      menu: payload.menu,
      config: payload.config,
      venue: payload.venue,
      autoKey: payload.autoKey || existing?.autoKey || null,
      createdAt: existing?.createdAt || now,
      updatedAt: now
    };
    const nextHistory = limitHistory([
      record,
      ...historyRecords.filter(item => item.id !== record.id)
    ]);
    setHistoryRecords(nextHistory);
    writeLocalHistory(nextHistory);
    return record;
  };

  const saveHistoryRecord = async ({ updateExisting = false } = {}) => {
    if (!menu.categories?.length) {
      setHistoryNotice('Add menu items before saving to history.');
      return;
    }

    const payload = buildHistoryPayload();
    const targetId = updateExisting ? activeHistoryId : null;

    try {
      const res = await fetch(targetId ? `/menu-history/${targetId}` : '/menu-history', {
        method: targetId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('History server unavailable');
      }

      const data = await res.json();
      const record = data.record;
      const nextHistory = limitHistory([
        record,
        ...historyRecords.filter(item => item.id !== record.id)
      ]);
      setHistoryRecords(nextHistory);
      writeLocalHistory(nextHistory);
      setActiveHistoryId(record.id);
      setHistoryTitle(record.title);
      setHistoryStatus(data.storage === 'cloud' ? 'server' : 'local');
      setHistoryNotice(updateExisting && targetId ? 'Updated saved feature menu.' : 'Saved feature menu to history.');
    } catch (err) {
      const record = upsertLocalHistoryRecord(payload, targetId);
      setActiveHistoryId(record.id);
      setHistoryTitle(record.title);
      setHistoryStatus('local');
      setHistoryNotice(updateExisting && targetId ? 'Updated local history copy.' : 'Saved to local history.');
    }
  };

  const autoSaveHistoryRecord = async (autoMeta) => {
    const payload = buildHistoryPayload(autoMeta);
    const existing = historyRecords.find(record => record.autoKey === autoMeta.autoKey);
    const targetId = existing?.id || (activeHistoryId && historyRecords.some(record => record.id === activeHistoryId && record.autoKey === autoMeta.autoKey) ? activeHistoryId : null);

    try {
      const res = await fetch(targetId ? `/menu-history/${targetId}` : '/menu-history', {
        method: targetId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('History server unavailable');
      }

      const data = await res.json();
      const record = data.record;
      const nextHistory = limitHistory([
        record,
        ...historyRecords.filter(item => item.id !== record.id)
      ]);
      setHistoryRecords(nextHistory);
      writeLocalHistory(nextHistory);
      setActiveHistoryId(record.id);
      setHistoryTitle(record.title);
      setHistoryStatus(data.storage === 'cloud' ? 'server' : 'local');
      setHistoryNotice(`Auto-saved as ${record.title}.`);
    } catch (err) {
      const record = upsertLocalHistoryRecord(payload, targetId);
      setActiveHistoryId(record.id);
      setHistoryTitle(record.title);
      setHistoryStatus('local');
      setHistoryNotice(`Auto-saved locally as ${record.title}.`);
    }
  };

  const loadHistoryRecord = (record, { duplicate = false } = {}) => {
    // Auto-save active menu before switching if modified
    const hasItems = menu.categories?.some(c => c.items?.length > 0);
    if (hasItems && activeHistoryId !== record.id) {
      const currentPayload = buildHistoryPayload();
      upsertLocalHistoryRecord(currentPayload);
    }

    const recordMenu = record.menu?.categories ? record.menu : { categories: [] };
    const recordConfig = {
      ...getDefaultConfig(),
      ...(record.config || {}),
      mealType: record.mealType || record.config?.mealType || 'Dinner Features'
    };

    setMenu(JSON.parse(JSON.stringify(recordMenu)));
    setConfig(recordConfig);
    if (record.venue) {
      onVenueChange({ ...venue, ...record.venue });
    }
    setHistoryTitle(duplicate ? `${record.title} Copy` : record.title);
    setActiveHistoryId(duplicate ? null : record.id);
    setSources([
      {
        ...createSource(),
        label: record.title,
        status: 'done',
        categories: JSON.parse(JSON.stringify(recordMenu.categories || []))
      }
    ]);
    setStep(2);
    setHistoryNotice(duplicate ? 'Loaded a copy into editor. Save it when ready.' : `Loaded version: ${record.title}`);
  };

  const deleteHistoryRecordFromLibrary = async (recordId) => {
    if (!window.confirm('Delete this saved feature menu from history?')) return;

    const nextHistory = historyRecords.filter(record => record.id !== recordId);
    setHistoryRecords(nextHistory);
    writeLocalHistory(nextHistory);

    if (activeHistoryId === recordId) {
      setActiveHistoryId(null);
    }

    try {
      const res = await fetch(`/menu-history/${recordId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 404) {
        throw new Error('History server unavailable');
      }
      setHistoryNotice('Deleted saved feature menu.');
    } catch (err) {
      setHistoryStatus('local');
      setHistoryNotice('Deleted local copy. Server history was unavailable.');
    }
  };

  const renderHistoryPanel = () => (
    <section className="history-panel">
      <div className="history-panel-head">
        <div>
          <p className="history-kicker">Saved Feature Menus</p>
          <h3>Reuse dinner features</h3>
        </div>
        <span className={`history-source ${historyStatus}`}>
          {historyStatus === 'server' ? 'Shared' : historyStatus === 'local' ? 'Local' : 'Loading'}
        </span>
      </div>

      {historyRecords.length === 0 ? (
        <p className="history-empty">No saved feature menus yet. Build one, then save it from the edit or preview screen.</p>
      ) : (
        <div className="history-list">
          {historyRecords.map(record => {
            const itemCount = (record.menu?.categories || []).reduce((sum, category) => sum + (category.items?.length || 0), 0);
            return (
              <article key={record.id} className="history-card">
                <div className="history-card-main">
                  <strong>{record.title}</strong>
                  <span>{record.mealType || 'Feature Menu'} · {itemCount} items</span>
                  <small>Updated {formatHistoryDate(record.updatedAt || record.createdAt)}</small>
                </div>
                <div className="history-card-actions">
                  <button type="button" className="secondary-btn mini-btn" onClick={() => loadHistoryRecord(record)}>
                    Edit
                  </button>
                  <button type="button" className="secondary-btn mini-btn" onClick={() => loadHistoryRecord(record, { duplicate: true })}>
                    Copy
                  </button>
                  <button type="button" className="history-delete-btn" onClick={() => deleteHistoryRecordFromLibrary(record.id)}>
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderHistorySaveBar = (compact = false) => (
    <div className={`history-save-bar ${compact ? 'compact' : ''}`}>
      <label>
        <span>History name</span>
        <input
          value={historyTitle}
          onChange={e => setHistoryTitle(e.target.value)}
          placeholder={getDefaultHistoryTitle()}
        />
      </label>
      <div className="history-save-actions">
        <button type="button" className="secondary-btn" onClick={() => saveHistoryRecord()}>
          Save New
        </button>
        <button
          type="button"
          className="primary-btn"
          onClick={() => saveHistoryRecord({ updateExisting: true })}
          disabled={!activeHistoryId}
        >
          Update Saved
        </button>
      </div>
      {historyNotice && <p className="history-notice">{historyNotice}</p>}
    </div>
  );

  const parsedCount = sources.filter(s => s.status === 'done').length;
  const availableMealTypes = [...new Set([...MEAL_TYPES, ...(venue?.meal_types || [])])];

  useEffect(() => {
    if (step !== 3 || !setHeaderControls) {
      if (setHeaderControls) setHeaderControls(null);
      return;
    }

    setHeaderControls({
      center: (
        <div className="top-nav-controls">
          <div className="top-control-group">
            <label className="top-control-label">Meal Type:</label>
            <select
              className="top-select"
              aria-label="Meal type"
              value={availableMealTypes.includes(config.mealType) ? config.mealType : 'custom'}
              onChange={e => {
                const val = e.target.value;
                setConfig(prev => ({ ...prev, mealType: val === 'custom' ? '' : val }));
              }}
            >
              {availableMealTypes.map(mt => (
                <option key={mt} value={mt}>{mt}</option>
              ))}
              <option value="custom">Custom...</option>
            </select>
          </div>

          <div className="top-control-group">
            <label className="top-control-label">Template Style:</label>
            <select
              className="top-select"
              aria-label="Template style"
              value={config.template}
              onChange={e => setConfig(prev => ({ ...prev, template: e.target.value }))}
            >
              {TEMPLATES.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      ),
      right: (
        <div className="top-nav-actions">
          <button className="secondary-btn" onClick={() => goBack(2)}>
            ← Back to Edit
          </button>
          <button className="secondary-btn" onClick={handlePrint} disabled={printLoading || pdfLoading}>
            {printLoading ? 'Preparing...' : '🖨️ Print'}
          </button>
          <button className="primary-btn" onClick={handleDownloadPDF} disabled={pdfLoading || pdfLoading}>
            {pdfLoading ? 'Generating...' : '⬇ Download PDF'}
          </button>
        </div>
      )
    });

    return () => {
      if (setHeaderControls) setHeaderControls(null);
    };
  }, [step, config.mealType, config.template, availableMealTypes, printLoading, pdfLoading, setHeaderControls]);

  return (
    <div className="generator-container animate-in">
      {/* Step indicator */}
      {step !== 3 && (
        <div className="step-indicator">
          {[
            { num: 1, label: 'Add Menus' },
            { num: 2, label: 'Edit' },
            { num: 3, label: 'Preview & Print' }
          ].map(({ num, label }) => (
            <div key={num} className={`step-dot ${step === num ? 'active' : ''} ${step > num ? 'completed' : ''}`}>
              <div className="dot-circle">{step > num ? '✓' : num}</div>
              <span className="dot-label">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── STEP 1: Add Sources ── */}
      {step === 1 && (
        <div className="step-panel">
          <h2 className="step-title">Add Your Menus</h2>
          <p className="step-sub">Upload photos or paste text. Add as many as you need — they'll be merged together.</p>

          <div className="add-menu-workspace">
            <aside className="history-rail">
              {renderHistoryPanel()}
            </aside>

            <div className="add-menu-main">
              <div className="sources-list">
                {sources.map((src, idx) => (
                  <MenuSourceCard
                    key={src.id}
                    source={src}
                    index={idx}
                    onUpdate={updateSource}
                    onRemove={sources.length > 1 ? () => removeSource(src.id) : null}
                  />
                ))}
              </div>

              <button className="add-source-btn" onClick={addSource}>
                + Add Another Menu
              </button>

              <button className="secondary-btn demo-menu-btn" onClick={loadDemoMenu}>
                Preview With Demo Menu
              </button>

              <div className="step-actions">
                <button
                  className="primary-btn"
                  disabled={parsedCount === 0}
                  onClick={goToEdit}
                >
                  Continue to Edit →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 2: Edit ── */}
      {step === 2 && (
        <div className="step-panel">
          <div className="editor-step-header">
            <div>
              <h2 className="step-title" style={{ marginTop: 0 }}>Edit Your Menu</h2>
              <p className="step-sub">Clean up names, prices, and descriptions. Add or remove items.</p>
            </div>
            <button className="secondary-btn top-back-btn" onClick={() => goBack(1)} style={{ padding: '8px 18px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              ← Back to Menus
            </button>
          </div>

          {renderHistorySaveBar()}

          <MenuEditor menu={menu} onChange={setMenu} />

          <div className="step-actions two-actions">
            <button className="secondary-btn" onClick={() => goBack(1)}>
              ← Back to Menus
            </button>
            <button
              className="primary-btn"
              disabled={menu.categories.length === 0}
              onClick={goToPreview}
            >
              Continue to Preview →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Preview & Download ── */}
      {step === 3 && (
        <div className="step-panel">
          <div className="preview-workspace">
            <aside className="preview-sidebar">
              <div className="preview-sidebar-card">
                <h2 className="step-title">Design & Settings</h2>
                <p className="step-sub">Customize typography and page layout for your menu.</p>

                {/* Print & Download Buttons inside Sidebar */}
                <div className="sidebar-action-row">
                  <button
                    type="button"
                    className="secondary-btn sidebar-action-btn"
                    onClick={handlePrint}
                    disabled={printLoading || pdfLoading}
                  >
                    {printLoading ? 'Preparing...' : '🖨️ Print'}
                  </button>
                  <button
                    type="button"
                    className="primary-btn sidebar-action-btn"
                    onClick={handleDownloadPDF}
                    disabled={pdfLoading || printLoading}
                  >
                    {pdfLoading ? 'Generating...' : '⬇ Download PDF'}
                  </button>
                </div>

                <ConfigPanel
                  config={config}
                  onChange={setConfig}
                  mealTypes={availableMealTypes}
                  categories={menu.categories.map(c => c.name)}
                  hideMealTypeAndTemplate={true}
                />

                {renderHistorySaveBar(true)}

                {/* Saved Versions Drawer */}
                <div className="version-history-drawer">
                  <div className="version-drawer-head" onClick={() => setShowHistoryDrawer(prev => !prev)}>
                    <span className="version-drawer-title">📜 Saved Versions ({historyRecords.length})</span>
                    <button type="button" className="version-drawer-toggle">
                      {showHistoryDrawer ? 'Hide ▲' : 'Show ▼'}
                    </button>
                  </div>
                  {showHistoryDrawer && (
                    <div className="version-drawer-body">
                      {historyRecords.length === 0 ? (
                        <p className="config-hint" style={{ margin: '0.4rem 0 0' }}>No saved versions yet. Save a version above!</p>
                      ) : (
                        <div className="version-mini-list">
                          {historyRecords.map(record => {
                            const itemCount = (record.menu?.categories || []).reduce((sum, c) => sum + (c.items?.length || 0), 0);
                            const isActive = record.id === activeHistoryId;
                            return (
                              <div key={record.id} className={`version-mini-item ${isActive ? 'active' : ''}`}>
                                <div className="version-mini-info">
                                  <strong>{record.title}</strong>
                                  <small>{record.mealType || 'Menu'} · {itemCount} items</small>
                                </div>
                                <div className="version-mini-actions">
                                  <button
                                    type="button"
                                    className="secondary-btn mini-btn"
                                    onClick={() => loadHistoryRecord(record)}
                                    title="Restore this saved version"
                                  >
                                    {isActive ? 'Active' : 'Restore'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </aside>

            <section className="preview-stage">
              <div className="preview-stage-head">
                <div className="preview-stage-meta">
                  <p className="preview-stage-kicker">Live Canvas</p>
                  <p className="preview-stage-note">Zoom controls adjust your display canvas scale.</p>
                </div>
                <div className="preview-zoom-controls">
                  <button
                    type="button"
                    className="spellcheck-btn"
                    onClick={handleSpellCheck}
                    disabled={spellChecking || menu.categories.length === 0}
                  >
                    {spellChecking ? 'Checking…' : 'Spell Check'}
                  </button>
                  <button
                    type="button"
                    className="zoom-btn"
                    onClick={() => setPreviewZoom(prev => Math.max(0.7, Math.round((prev - 0.1) * 10) / 10))}
                  >
                    -
                  </button>
                  <select
                    className="zoom-select"
                    aria-label="Preview zoom"
                    value={previewZoom}
                    onChange={event => setPreviewZoom(Number(event.target.value))}
                  >
                    <option value={0.7}>70%</option>
                    <option value={0.85}>85%</option>
                    <option value={1}>100%</option>
                    <option value={1.15}>115%</option>
                    <option value={1.3}>130%</option>
                  </select>
                  <button
                    type="button"
                    className="zoom-btn"
                    onClick={() => setPreviewZoom(prev => Math.min(1.3, Math.round((prev + 0.1) * 10) / 10))}
                  >
                    +
                  </button>
                </div>
              </div>

              <MenuPreview
                menu={menu}
                venue={venue}
                config={config}
                zoom={previewZoom}
                containerRef={previewContainerRef}
              />
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
